import React from 'react';
import axios from 'axios';

import { getAxiosParamsSerializer, reportAxiosError } from '~/Utils';

import { useFileDownload } from '../../hooks/useFileDownload';
import useDataFetcher from '../../useDataFetcher';
import { useSysconfig } from '../SystemConfigurationScreen';

import CoveragesConfigurations from './CoveragesConfigurations';

const ExposureConfigurationToolBackendWrapper = () => {
  const {
    organization,
    organizationOperationalDetails: { supportedClaimTypes, coverageConfiguration },
    reloadOperationalDetails,
  } = useSysconfig();

  const baseCoveragesRoute = `/sysconfig/api/v1/organizations/${organization.id}/coverages`;
  const {
    isLoading,
    isError,
    data: coverages = [],
    reloadData: reloadCoverages,
  } = useDataFetcher(baseCoveragesRoute, { params: { include_disabled: true } });

  const baseSharedCoveragesRoute = `/sysconfig/api/v1/organizations/${organization.id}/shared_coverages`;
  const {
    isLoading: isLoadingShared,
    isError: isErrorShared,
    data: sharedCoverages,
    reloadData: reloadSharedCoverages,
  } = useDataFetcher(baseSharedCoveragesRoute);

  const callAPI = async (method, url, values) => {
    await axios({ method, url, data: values });
  };

  const callAPIWithCatch = async (method, url, values) => {
    try {
      await callAPI(method, url, values);
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const handleCreateCoverage = async (values) => {
    await callAPIWithCatch('post', baseCoveragesRoute, values);
    await reloadCoverages();
  };

  const handleUpdateCoverage = async (values) => {
    // Only send fields allowed by CoverageConfigUpdateParams
    const {
      coverage_key,
      display_name,
      sub_reserves_config,
      iso_code,
      sub_organization_ids,
      involved_types,
      coverage_group_key,
      use_indemnity_sub_reserves,
      expense_policy_limit_config,
      include_depreciation,
      depreciation_calculation_api_url,
    } = values;

    const updatePayload = {
      display_name,
      sub_reserves_config,
      iso_code,
      sub_organization_ids,
      involved_types,
      coverage_group_key,
      use_indemnity_sub_reserves,
      expense_policy_limit_config,
      include_depreciation,
      depreciation_calculation_api_url,
    };

    await callAPIWithCatch('patch', `${baseCoveragesRoute}/${coverage_key}`, updatePayload);
    await reloadCoverages();
  };

  const handleCreateSharedCoverage = async (values) => {
    await callAPIWithCatch('post', baseSharedCoveragesRoute, values);
    await reloadSharedCoverages();
  };

  const handleUpdateSharedCoverage = async (values) => {
    // Only send fields allowed by SharedCoverageConfigUpdateParams
    const { shared_coverage_key, sub_organization_ids } = values;

    await callAPIWithCatch('patch', `${baseSharedCoveragesRoute}/${shared_coverage_key}`, { sub_organization_ids });
    await reloadSharedCoverages();
  };

  const handleToggleGeneralExpenses = async (generalExpensesConfigState) => {
    try {
      await axios.post(`${baseCoveragesRoute}/configuration/general_expenses_config`, {
        is_general_expenses_disabled: generalExpensesConfigState.is_general_expenses_disabled,
        is_general_expenses_disabled_per_sub_org: generalExpensesConfigState?.is_general_expenses_disabled_per_sub_org,
      });
      await reloadOperationalDetails();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const handleUpdateCoverageConfiguration = async (fieldName, checked) => {
    try {
      await axios.patch(`${baseCoveragesRoute}/configuration`, { [fieldName]: checked });
      await reloadOperationalDetails();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const fetchKeys = (payload) =>
    axios.get(`/sysconfig/api/v1/organizations/${organization.id}/coverages/keys_used`, {
      params: payload,
      paramsSerializer: getAxiosParamsSerializer('bracket'),
    });

  const handleFetchUsedSharedCoveragesKeys = ({ shared_coverage_keys_to_ignore }) =>
    fetchKeys({
      shared_coverage_keys_to_ignore,
    });

  const handleFetchUsedCoveragesKeys = ({ coverage_keys_to_ignore }) =>
    fetchKeys({
      coverage_keys_to_ignore,
    });

  const handleFetchCoverages = async ({ subOrganizationIds, lob }) => {
    const { data } = await axios.get(`/api/v1/organizations/${organization.id}/coverage_configs`, {
      params: { sub_organization_ids: subOrganizationIds, lob },
    });
    return data;
  };

  // The hook owns the in-flight guard, the save-to-disk mechanics and the blob-error reporting; this
  // wrapper owns the request and the fallback filename, so the filters stay where they are built.
  const { download: downloadExport, isDownloading: isExporting } = useFileDownload();

  const handleExportToExcel = ({ subOrganizationIds, searchText, lobs } = {}) =>
    downloadExport(
      // Only non-empty filters are sent: the query schema forbids extras but treats an omitted
      // parameter as "no narrowing", and an empty array would serialize to nothing useful anyway.
      () =>
        axios.get(`${baseCoveragesRoute}/export`, {
          responseType: 'blob',
          params: {
            ...(subOrganizationIds?.length ? { sub_organization_ids: subOrganizationIds } : {}),
            ...(lobs?.length ? { lobs } : {}),
            ...(searchText ? { search_text: searchText } : {}),
          },
        }),
      // Only reached if the route stops sending Content-Disposition; the server's own name carries
      // the export date, which this cannot.
      `${organization.external_id}_coverage_configuration.xlsx`
    );

  const handleDisable = async (config) => {
    try {
      await callAPI('patch', `${baseCoveragesRoute}/${config.coverage_key}/disable`, { is_disabled: true });
      await reloadCoverages();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  return (
    <CoveragesConfigurations
      isLoading={isLoading || isLoadingShared}
      isError={isError || isErrorShared}
      organization={organization}
      supportedClaimTypes={supportedClaimTypes}
      coverages={coverages}
      sharedCoverages={sharedCoverages}
      coverageConfiguration={coverageConfiguration}
      onUpdateCoverage={handleUpdateCoverage}
      onCreateCoverage={handleCreateCoverage}
      onUpdateSharedCoverage={handleUpdateSharedCoverage}
      onCreateSharedCoverage={handleCreateSharedCoverage}
      onToggleGeneralExpenses={handleToggleGeneralExpenses}
      onUpdateCoverageConfiguration={handleUpdateCoverageConfiguration}
      fetchUsedCoveragesKeys={handleFetchUsedCoveragesKeys}
      fetchUsedSharedCoveragesKeys={handleFetchUsedSharedCoveragesKeys}
      fetchCoverages={handleFetchCoverages}
      errorHandler={reportAxiosError}
      onDisable={handleDisable}
      onExportToExcel={handleExportToExcel}
      isExporting={isExporting}
    />
  );
};
export { ExposureConfigurationToolBackendWrapper };
